import React, { useState, useEffect } from 'react';
import { FaBolt, FaMoneyBillWave, FaCalendarAlt, FaHome, FaPiggyBank, FaShieldAlt, FaClock } from 'react-icons/fa';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const boxStyle = {
  background: '#e0f2f1',
  borderLeft: '6px solid #00796b',
  padding: '15px 20px',
  borderRadius: 10,
  width: 150,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const titleStyle = {
  margin: '10px 0 5px',
  color: '#004d40',
  fontWeight: '600',
};

const valueStyle = {
  fontSize: '1.2rem',
  fontWeight: '700',
  color: '#00796b',
};

const ResultBox = ({ icon, title, value }) => (
  <div style={boxStyle}>
    {icon}
    <h5 style={titleStyle}>{title}</h5>
    <p style={valueStyle}>{value}</p>
  </div>
);

const SolarCalculator = () => {
  const [sanctionLoad, setSanctionLoad] = useState('');
  const [monthlyUnits, setMonthlyUnits] = useState('');
  const [biMonthlyBill, setBiMonthlyBill] = useState('');
  const [usageType, setUsageType] = useState('domestic');
  const [results, setResults] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCalculate = () => {
    const unitCostWithTax = 6 * 1.18;

    let _sanctionLoad = parseFloat(sanctionLoad);
    let _monthlyUnits = parseFloat(monthlyUnits);
    let _biMonthlyBill = parseFloat(biMonthlyBill);

    if (isNaN(_sanctionLoad) && isNaN(_monthlyUnits) && isNaN(_biMonthlyBill)) {
      alert("Please enter at least one value to calculate.");
      return;
    }

    if (!isNaN(_biMonthlyBill)) {
      const oneMonthBill = _biMonthlyBill / 2;
      _monthlyUnits = oneMonthBill / unitCostWithTax;
      _sanctionLoad = _monthlyUnits / 120;
    } else if (!isNaN(_monthlyUnits)) {
      _sanctionLoad = _monthlyUnits / 120;
    } else if (!isNaN(_sanctionLoad)) {
      _monthlyUnits = _sanctionLoad * 120;
    }

    const monthlyBill = _monthlyUnits * unitCostWithTax;
    const yearlyUnits = _monthlyUnits * 12;
    const costSaving = monthlyBill * 12;

    const panelPerKw = 2;
    const areaPerPanel = 7 * 4;
    const roofArea = _sanctionLoad * panelPerKw * areaPerPanel;

    const investment = _sanctionLoad * 60000;

    let subsidy = 0;
      if (usageType === 'domestic') {
        const roundedSanctionLoad = Math.round(_sanctionLoad);

        if (roundedSanctionLoad === 1) {
          subsidy = 30000;
        } else if (roundedSanctionLoad === 2) {
          subsidy = 60000;
        } else if (roundedSanctionLoad >= 3) {
          subsidy = 78000;
        }
      }
     

    const returnYears = (investment - subsidy) / costSaving;

    setResults({

      sanctionLoad: _sanctionLoad.toFixed(2),
      monthlyUnits: _monthlyUnits.toFixed(0),
      monthlyBill: monthlyBill.toFixed(0),
      yearlyUnits: yearlyUnits.toFixed(0),
      costSaving: costSaving.toFixed(0),
      roofArea: roofArea.toFixed(0),
      investment: investment.toFixed(0),
      subsidy: subsidy.toFixed(0),
      returnYears: returnYears.toFixed(1),
    });
  };

  const renderResultBoxes = () => (
    <>
      <ResultBox icon={<FaBolt size={30} color="#00796b" />} title="Sanction Load" value={`${results.sanctionLoad} kW`} />
      <ResultBox icon={<FaCalendarAlt size={30} color="#00796b" />} title="Monthly Units" value={`${results.monthlyUnits} units`} />
      <ResultBox icon={<FaMoneyBillWave size={30} color="#00796b" />} title="Monthly Bill" value={`₹${results.monthlyBill}`} />
      <ResultBox icon={<FaCalendarAlt size={30} color="#00796b" />} title="Yearly Units" value={`${results.yearlyUnits} units`} />
      <ResultBox icon={<FaPiggyBank size={30} color="#00796b" />} title="Cost Saving" value={`₹${results.costSaving} / year`} />
      <ResultBox icon={<FaHome size={30} color="#00796b" />} title="Roof Area Needed" value={`${results.roofArea} sq.ft`} />
      <ResultBox icon={<FaMoneyBillWave size={30} color="#00796b" />} title="Investment" value={`₹${results.investment}`} />
      <ResultBox icon={<FaShieldAlt size={30} color="#00796b" />} title="Subsidy" value={`₹${results.subsidy}`} />
      <ResultBox icon={<FaClock size={30} color="#00796b" />} title="Payback Period" value={`${results.returnYears} years`} />
    </>
  );

  return (
      <div style={{ width: '100%', maxWidth: '2500px', margin: '20px auto' }}>
    {/* Main content container */}
    <div
      style={{
        display: isMobile ? 'block' : 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 30,
        padding: 25,
        background: 'white',
        borderRadius: 12,
        boxSizing: 'border-box',
      }}
    >

      {/* Calculator Form */}
      <div style={{ flex: 1, minWidth: 150, maxWidth: 800,  width: '100%' }}>
        <h2 style={{ color: '#00796b', textAlign: 'center', marginBottom: 20, fontSize:25 }}>Solar Calculator</h2>

        <label>Sanction Load (kW):</label>
        <input
          type="number"
          value={sanctionLoad}
          onChange={e => setSanctionLoad(e.target.value)}
          placeholder="e.g., 2"
          style={{ width: '100%', padding: 10, borderRadius: 6, marginBottom: 10 }}
        />

        <label>Monthly Units Consumption:</label>
        <input
          type="number"
          value={monthlyUnits}
          onChange={e => setMonthlyUnits(e.target.value)}
          placeholder="e.g., 240"
          style={{ width: '100%', padding: 10, borderRadius: 6, marginBottom: 10 }}
        />

        <label>2-Month Electricity Bill (₹):</label>
        <input
          type="number"
          value={biMonthlyBill}
          onChange={e => setBiMonthlyBill(e.target.value)}
          placeholder="e.g., 3400"
          style={{ width: '100%', padding: 10, borderRadius: 6, marginBottom: 10 }}
        />

        <label>Usage Type:</label>
        <select
          value={usageType}
          onChange={e => setUsageType(e.target.value)}
          style={{ width: '100%', padding: 10, borderRadius: 6 }}
        >
          <option value="domestic">Domestic</option>
          <option value="commercial">Commercial</option>
        </select>

        <button
          onClick={handleCalculate}
          style={{
            width: '100%',
            marginTop: 20,
            padding: 14,
            backgroundColor: '#00796b',
            color: 'white',
            fontSize: 18,
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer'
          }}
        >
          Calculate
        </button>
      </div>

      {/* Results Section */}
      {results && (
        <div
          style={{
          flex: 1,
          maxWidth: 1200,
          width: '100%',
          overflowY: 'visible',
          padding: 10,
        }}
        >
          <h3 style={{ marginBottom: 20, color: '#00796b', textAlign: isMobile ? 'center' : 'left',maxWidth: 500, }}>Results</h3>

          {isMobile ? (
            <Slider dots={true} infinite={false} speed={500} slidesToShow={1} slidesToScroll={1}>
              {renderResultBoxes()}
            </Slider>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 15,
            }}>
              {renderResultBoxes()}
            </div>
          )}
        </div>
       
      )}
      </div>
      <div
          style={{
            marginTop: 20,
            padding: '15px 25px',
            backgroundColor: '#f0f0f0',
            borderRadius: 8,
            textAlign: 'center',
          }}
        >
          <p style={{ fontSize: 14, color: '#555', margin: 0 }}>
            <strong>Disclaimer:</strong> The calculations provided here are approximate and based on assumed
            electricity rates and typical usage patterns. Actual values may vary depending on your electricity
            provider (EB) rates and other factors. This tool is intended for estimation and verification purposes
            only and should not be considered as final or permanent financial advice.
          </p>
        </div>
    </div> 
  );
  
};

export default SolarCalculator;











